import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FolderOpen, ArrowDownUp } from 'lucide-react';
import { useProjectsStore } from '../stores/projects-store';
import { useAuthStore } from '../stores/auth-store';
import { Button } from '../components/ui/Button';
import { Dialog } from '../components/ui/Dialog';
import clsx from 'clsx';
import { AuthButton } from '../components/auth/AuthButton';

type TemplateType = 'general' | 'landing' | 'mobile';

function tsToMs(ts: any): number {
  const sec = ts?.seconds ?? 0;
  return sec * 1000;
}

function formatDate(ts: any): string {
  const ms = tsToMs(ts);
  if (!ms) return '';
  return new Date(ms).toLocaleString();
}

export function DashboardPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const projects = useProjectsStore((s) => s.projects);
  const createProject = useProjectsStore((s) => s.createProject);

  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [template, setTemplate] = useState<TemplateType>('general');
  const [canvasWidth, setCanvasWidth] = useState(1440);
  const [canvasHeight, setCanvasHeight] = useState(3000);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (template === 'landing') {
      setCanvasWidth(1440);
      setCanvasHeight(3000);
    } else if (template === 'mobile') {
      setCanvasWidth(390);
      setCanvasHeight(844);
    }
  }, [template]);

  const sorted = useMemo(() => {
    const list = [...projects];
    list.sort((a: any, b: any) => {
      const am = tsToMs(a.updatedAt) || tsToMs(a.createdAt);
      const bm = tsToMs(b.updatedAt) || tsToMs(b.createdAt);
      return sortDir === 'desc' ? bm - am : am - bm;
    });
    return list;
  }, [projects, sortDir]);

  const openProject = (projectId: string) => {
    navigate(`/editor/${projectId}`);
  };

  const handleCreate = async () => {
    if (!user) return;
    const trimmed = name.trim();
    if (!trimmed) return;

    setCreating(true);
    try {
      const id = await createProject(user.uid, trimmed, {
        templateType: template,
        canvasWidth: template === 'general' ? undefined : canvasWidth,
        canvasHeight: template === 'general' ? undefined : canvasHeight,
      });
      setCreateOpen(false);
      setName('');
      openProject(id);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface text-text">
      <div className="border-b border-surface-lighter bg-surface-light">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-semibold text-text">Cabinet</div>
            <div className="text-xs text-text-muted">My projects</div>
          </div>

          <div className="flex items-center gap-2">
            <AuthButton />
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded border border-surface-lighter bg-surface px-3 py-2 text-xs text-text hover:bg-surface-lighter transition-colors"
              onClick={() => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))}
              title="Sort by date"
            >
              <ArrowDownUp size={14} className="text-text-muted" />
              {sortDir === 'desc' ? 'Newest' : 'Oldest'}
            </button>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus size={14} />
              <span>Create project</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {sorted.length === 0 ? (
          <div className="rounded-xl border border-surface-lighter bg-surface-light p-8 text-center">
            <div className="text-sm font-semibold text-text">No projects yet</div>
            <div className="mt-1 text-xs text-text-muted">Create your first project to start editing.</div>
            <div className="mt-5 flex justify-center">
              <Button onClick={() => setCreateOpen(true)}>
                <Plus size={14} />
                <span>Create project</span>
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {sorted.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => openProject(p.id)}
                className="text-left rounded-xl border border-surface-lighter bg-surface-light hover:bg-surface transition-colors p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <FolderOpen size={14} className="text-accent shrink-0" />
                      <div className="text-sm font-semibold text-text truncate">{p.name}</div>
                    </div>
                    <div className="mt-1 text-[11px] text-text-muted">
                      Updated: {formatDate(p.updatedAt) || formatDate(p.createdAt)}
                    </div>
                  </div>
                  <div className="text-[11px] text-text-muted shrink-0">
                    {(p.templateType ?? 'general').toUpperCase()}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} title="Create project">
        <div className="space-y-5">
          <div className="space-y-2">
            <label className="text-xs font-semibold text-text-muted">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Project name..."
              className="w-full px-2 py-2 bg-surface border border-surface-lighter rounded text-xs text-text placeholder:text-text-muted focus:outline-none focus:border-accent"
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold text-text-muted">Template</label>
            <div className="grid grid-cols-3 gap-2">
              {([
                { id: 'general', label: 'General' },
                { id: 'landing', label: 'Landing' },
                { id: 'mobile', label: 'Mobile app' },
              ] as Array<{ id: TemplateType; label: string }>).map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => setTemplate(it.id)}
                  className={clsx(
                    'rounded-lg border px-3 py-2 text-xs font-semibold transition-colors',
                    template === it.id
                      ? 'border-accent bg-accent/20 text-accent'
                      : 'border-surface-lighter bg-surface text-text hover:bg-surface-lighter'
                  )}
                >
                  {it.label}
                </button>
              ))}
            </div>
            <div className="text-[11px] text-text-muted">
              General: current behavior. Landing/Mobile: fixed canvas size.
            </div>
          </div>

          {template !== 'general' && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-xs font-semibold text-text-muted">Canvas width (px)</label>
                <input
                  type="number"
                  value={canvasWidth}
                  onChange={(e) => setCanvasWidth(Number(e.target.value))}
                  className="w-full px-2 py-2 bg-surface border border-surface-lighter rounded text-xs text-text focus:outline-none focus:border-accent"
                  min={240}
                  max={4096}
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-semibold text-text-muted">Canvas height (px)</label>
                <input
                  type="number"
                  value={canvasHeight}
                  onChange={(e) => setCanvasHeight(Number(e.target.value))}
                  className="w-full px-2 py-2 bg-surface border border-surface-lighter rounded text-xs text-text focus:outline-none focus:border-accent"
                  min={240}
                  max={20000}
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={creating || !name.trim()}>
              {creating ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
